import { reactive } from "vue";
import api from "./api";
import { isSwitchedOff } from "./accountGate";

// One reactive singleton for "who is signed in", read by App.vue's gate and
// by anything that wants to show whose pick is whose later. No Vuex/Pinia in
// this app -- a plain reactive object is enough for state this small.
const TOKEN_KEY = "fivepicks_token";

const state = reactive({
  user: null,
  token: localStorage.getItem(TOKEN_KEY) || null,
  // False until the initial restore-from-storage attempt has finished, so the
  // gate can show a loading state instead of flashing the sign-in screen for
  // a picker who is actually already signed in.
  ready: false,
  // True once any signed-in request comes back as a switched-off account's
  // generic error (see accountGate.js). App.vue then replaces the whole shell
  // with "Something went wrong" on every route. The token is kept: the account
  // is still signed in, and switching it back on works on the next load.
  unavailable: false,
});

function applyAuthHeader() {
  if (state.token) {
    api.defaults.headers.common["Authorization"] = `Bearer ${state.token}`;
  } else {
    delete api.defaults.headers.common["Authorization"];
  }
}

function setSession(token, user) {
  state.token = token;
  state.user = user;
  localStorage.setItem(TOKEN_KEY, token);
  applyAuthHeader();
}

function clearSession() {
  state.token = null;
  state.user = null;
  localStorage.removeItem(TOKEN_KEY);
  applyAuthHeader();
}

// Called on app boot. A stored token is optimistic -- it may have been
// revoked or simply expired since the last visit -- so this always confirms
// it against the backend rather than trusting localStorage on its own.
//
// Reentrant: App.vue's mount and the router's admin guard both call this, and
// they must resolve the same single /session request rather than firing two.
let restorePromise = null;

function restoreSession() {
  restorePromise ||= doRestore();
  return restorePromise;
}

async function doRestore() {
  applyAuthHeader();

  if (!state.token) {
    state.ready = true;
    return;
  }

  try {
    const { data } = await api.get("/api/v1/session");
    state.user = data.user;
  } catch (error) {
    // A switched-off account keeps its token -- clearing it would show the
    // sign-in screen, which reads as a ban rather than an outage.
    if (!isSwitchedOff(error)) clearSession();
  } finally {
    state.ready = true;
  }
}

// `credential` is the ID token Google's Sign In button hands back.
async function signInWithGoogle(credential) {
  const { data } = await api.post("/api/v1/session", { credential });
  setSession(data.token, data.user);
}

// Development-only: skip Google entirely and sign in as the first user, so the
// SPA can be opened on a phone over the LAN where Google's authorized origins
// don't reach. The backend rejects `dev` outside development.
async function signInDev() {
  const { data } = await api.post("/api/v1/session", { dev: true });
  setSession(data.token, data.user);
}

// Update the signed-in picker's own profile (currently just the pool-name
// link). Keeps the reactive `state.user` in step so anything watching it
// re-renders.
async function updateProfile(fields) {
  const { data } = await api.patch("/api/v1/session", fields);
  state.user = data.user;
  return data.user;
}

async function signOut() {
  try {
    await api.delete("/api/v1/session");
  } catch (error) {
    // Already gone (expired, or deleted from another tab) -- clearing the
    // local copy is still the right outcome either way.
  }
  clearSession();
}

// A session can go bad mid-visit (expired, or signed out from another tab) --
// catch that anywhere it surfaces, not just on the initial restore, so the
// app falls back to the sign-in screen instead of spinning on a 401 forever.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) clearSession();
    if (isSwitchedOff(error)) state.unavailable = true;
    return Promise.reject(error);
  }
);

export default { state, restoreSession, signInWithGoogle, signInDev, updateProfile, signOut, clearSession };
